import { z } from 'zod';
import { PERMISSIONS, ROLE_PERMISSIONS, ROLES, type Permission } from './auth.js';

/**
 * Access control.
 *
 * The built-in roles in `auth.ts` remain the seed data, but they are no longer
 * the whole story: at runtime a user's permissions come from the database, so
 * an operator can create roles, group people, and grant or revoke a single
 * capability for one person without editing code.
 *
 * Resolution order — and `deny` winning is the important part:
 *
 *     role grants ∪ group role grants ∪ user allows  −  user denies
 *
 * Without a deny layer, revoking one capability from one person means cloning
 * a whole role for them, which is how permission systems rot into dozens of
 * near-identical roles.
 */

export const roleSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(60),
  description: z.string().default(''),
  permissions: z.array(z.enum(PERMISSIONS)).default([]),
  builtin: z.boolean().default(false),
  customerId: z.string().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type CustomRole = z.infer<typeof roleSchema>;

export const roleCreateSchema = roleSchema.omit({
  id: true,
  builtin: true,
  createdAt: true,
  updatedAt: true,
});
export type RoleCreate = z.infer<typeof roleCreateSchema>;

export const userGroupSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(60),
  description: z.string().default(''),
  customerId: z.string().nullable().default(null),
  roleIds: z.array(z.string()).default([]),
  memberIds: z.array(z.string()).default([]),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type UserGroup = z.infer<typeof userGroupSchema>;

export const userGroupCreateSchema = userGroupSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type UserGroupCreate = z.infer<typeof userGroupCreateSchema>;

export const PERMISSION_EFFECTS = ['allow', 'deny'] as const;
export type PermissionEffect = (typeof PERMISSION_EFFECTS)[number];

export const userPermissionSchema = z.object({
  permission: z.enum(PERMISSIONS),
  effect: z.enum(PERMISSION_EFFECTS),
  reason: z.string().nullable().default(null),
});
export type UserPermissionOverride = z.infer<typeof userPermissionSchema>;

/** Everything that contributed to a user's effective permission set. */
export interface EffectivePermissions {
  permissions: Permission[];
  /** Where each granted permission came from — shown in the UI so an admin can
   *  see *why* someone can do something, not just that they can. */
  sources: Record<string, string[]>;
  denied: Permission[];
  roleNames: string[];
  groupNames: string[];
}

export function resolvePermissions(input: {
  rolePermissions: Permission[];
  roleName?: string;
  groupGrants: Array<{ groupName: string; permissions: Permission[] }>;
  overrides: UserPermissionOverride[];
}): EffectivePermissions {
  const sources: Record<string, string[]> = {};
  const granted = new Set<Permission>();

  const add = (permission: Permission, source: string) => {
    granted.add(permission);
    (sources[permission] ??= []).push(source);
  };

  for (const permission of input.rolePermissions) add(permission, `role:${input.roleName ?? 'primary'}`);
  for (const group of input.groupGrants) {
    for (const permission of group.permissions) add(permission, `group:${group.groupName}`);
  }
  for (const override of input.overrides) {
    if (override.effect === 'allow') add(override.permission, 'user:granted');
  }

  // Denies are applied last and unconditionally — an explicit revocation must
  // not be undone by the user also being in a group that grants it.
  const denied: Permission[] = [];
  for (const override of input.overrides) {
    if (override.effect === 'deny') {
      granted.delete(override.permission);
      delete sources[override.permission];
      denied.push(override.permission);
    }
  }

  return {
    permissions: [...granted],
    sources,
    denied,
    roleNames: input.roleName ? [input.roleName] : [],
    groupNames: input.groupGrants.map((g) => g.groupName),
  };
}

/**
 * Permissions grouped for the role editor.
 *
 * A flat checklist of 30 permissions is unusable; grouping them by the thing
 * they act on is what makes "what does this role actually do" answerable at a
 * glance.
 */
export const PERMISSION_GROUPS: Array<{ label: string; permissions: Permission[] }> = [
  { label: 'Resources', permissions: ['resource:read', 'resource:write', 'metric:read'] },
  { label: 'Alerts', permissions: ['alert:read', 'alert:ack', 'alert:write'] },
  { label: 'Service desk', permissions: ['ticket:read', 'ticket:write', 'ticket:assign'] },
  { label: 'Billing', permissions: ['billing:read', 'billing:write', 'utility:write'] },
  { label: 'Password safe', permissions: ['vault:read', 'vault:reveal', 'vault:write', 'vault:manage'] },
  { label: 'Backups', permissions: ['backup:read', 'backup:write', 'backup:run', 'backup:restore'] },
  { label: 'Diagnostics', permissions: ['tools:run', 'tools:scan', 'tools:proxy', 'tools:provision', 'access:ticket'] },
  { label: 'Administration', permissions: ['user:read', 'user:manage', 'plugin:read', 'plugin:manage', 'theme:read', 'theme:manage', 'settings:manage', 'audit:read'] },
  { label: 'Client team', permissions: ['client_user:manage'] },
];

/** Human-readable descriptions, so the role editor explains what it is granting. */
export const PERMISSION_LABELS: Partial<Record<Permission, string>> = {
  'resource:read': 'View monitored resources and their health',
  'resource:write': 'Add, edit and delete resources',
  'metric:read': 'View metric history and charts',
  'alert:read': 'View alerts',
  'alert:ack': 'Acknowledge alerts',
  'alert:write': 'Resolve alerts and edit alert rules',
  'ticket:read': 'View tickets',
  'ticket:write': 'Create and reply to tickets',
  'ticket:assign': 'Assign tickets and change priority',
  'billing:read': 'View balances, invoices and subscriptions',
  'billing:write': 'Post transactions, issue invoices, manage subscriptions',
  'vault:read': 'See that a credential exists — not its value',
  'vault:reveal': 'Reveal stored secrets (audited)',
  'vault:write': 'Create, edit and rotate credentials',
  'vault:manage': 'View the credential access log',
  'backup:read': 'View backup plans and runs',
  'backup:write': 'Create and edit backup plans and storage',
  'backup:run': 'Trigger a backup on demand',
  'backup:restore': 'Restore a backup onto a live database or device (overwrites current state)',
  'tools:run': 'Ping, traceroute, DNS and HTTP diagnostics',
  'tools:scan': 'Port scans and subnet sweeps',
  'tools:proxy': "Open a device's admin interface through the server",
  'tools:provision': 'Log into a device to enable a service and open the firewall for it (writes device config)',
  'access:ticket': "Open a terminal on a client's devices while assigned to one of its open tickets — the server logs in, the password is never shown",
  'user:read': 'View users',
  'user:manage': 'Create, edit and remove users, roles and groups',
  'plugin:read': 'View installed plugins',
  'plugin:manage': 'Enable, disable and configure plugins',
  'theme:read': 'Use themes',
  'theme:manage': 'Create and edit themes',
  'settings:manage': 'Server settings and notification testing',
  'audit:read': 'View the audit log',
  'utility:write': 'Create, edit, record payments for and delete utility bills',
  'client_user:manage': "Invite, edit and remove users within your own client account (viewer/admin only)",
};

/** Seeds for the built-in roles, so they exist as editable rows. */
export const BUILTIN_ROLE_SEEDS = ROLES.map((role) => ({
  id: `role_${role}`,
  name: role,
  description:
    role === 'owner'
      ? 'Full access, including instance ownership'
      : role === 'admin'
        ? 'Full administrative access'
        : role === 'engineer'
          ? 'Monitor, troubleshoot and fix; can reveal credentials'
          : role === 'technician'
            ? "Works assigned tickets; reaches that client's devices through server-filled logins, never seeing a password"
          : role === 'billing'
            ? 'Accounts, invoices and subscriptions'
            : role === 'viewer'
              ? 'Read-only; cannot reveal secrets'
              : role === 'customer_admin'
                ? "Client portal access with admin rights over their own tenant's resources, tickets, credentials, utility bills and team"
                : 'Customer portal access, scoped to their own tenant',
  permissions: [...ROLE_PERMISSIONS[role]] as Permission[],
}));
